export const filtersStatic: Record<
  string,
  {
    label: string;
    emoji: string;
    question: string;
    answers: {
      great: string;
      adequate: string;
      couldImprove: string;
      notAvailable: string;
      notSure: string;
    };
  }
> = {
  // Connectivity & Work
  wifi: {
    label: 'Wi-Fi',
    emoji: '📶',
    question: 'Wi-Fi good enough for work?',
    answers: {
      great: 'Lightning fast',
      adequate: 'Good enough',
      couldImprove: 'Bit slow',
      notAvailable: 'No wifi',
      notSure: 'Not sure',
    },
  },
  power_sockets: {
    label: 'Power sockets',
    emoji: '🔌',
    question: 'Easy to find a plug?',
    answers: {
      great: 'Easy to find',
      adequate: 'Available nearby',
      couldImprove: 'Hard to find',
      notAvailable: 'No outlets',
      notSure: 'Not sure',
    },
  },
  long_stays_allowed: {
    label: 'Long stays',
    emoji: '⏰',
    question: 'Feel welcome to stay long?',
    answers: {
      great: 'Very welcome',
      adequate: 'Pretty welcome',
      couldImprove: 'Bit rushed',
      notAvailable: 'Time limited',
      notSure: 'Not sure',
    },
  },
  work_friendly_tables: {
    label: 'Work tables',
    emoji: '💻',
    question: 'Comfy setup for working?',
    answers: {
      great: 'Very comfortable',
      adequate: 'Pretty good',
      couldImprove: 'Bit cramped',
      notAvailable: 'No work space',
      notSure: 'Not sure',
    },
  },
  quiet: {
    label: 'Quiet',
    emoji: '🤫',
    question: 'Quiet enough to focus?',
    answers: {
      great: 'Very peaceful',
      adequate: 'Reasonably quiet',
      couldImprove: 'Bit noisy',
      notAvailable: 'Too loud',
      notSure: 'Not sure',
    },
  },
  call_friendly: {
    label: 'Call-friendly',
    emoji: '📞',
    question: 'OK to take calls here?',
    answers: {
      great: 'Very welcome',
      adequate: 'Pretty OK',
      couldImprove: 'Bit awkward',
      notAvailable: 'No calls allowed',
      notSure: 'Not sure',
    },
  },
  printer_scanner: {
    label: 'Printer / Scanner',
    emoji: '🖨️',
    question: 'Printer/scanner available?',
    answers: {
      great: 'Easy to use',
      adequate: 'Available',
      couldImprove: 'Hard to access',
      notAvailable: 'Not available',
      notSure: 'Not sure',
    },
  },
  private_rooms: {
    label: 'Private rooms',
    emoji: '🚪',
    question: 'Private spaces when needed?',
    answers: {
      great: 'Always available',
      adequate: 'Usually free',
      couldImprove: 'Often busy',
      notAvailable: 'No private rooms',
      notSure: 'Not sure',
    },
  },

  // Atmosphere
  crowd_level: {
    label: 'Not crowded',
    emoji: '👥',
    question: 'Enough room to work comfortably?',
    answers: {
      great: 'Plenty of space',
      adequate: 'Comfortable',
      couldImprove: 'Quite busy',
      notAvailable: 'Packed',
      notSure: 'Not sure',
    },
  },
  people_working: {
    label: 'People working',
    emoji: '💼',
    question: 'Others working here too?',
    answers: {
      great: 'Lots of workers',
      adequate: 'Some workers',
      couldImprove: 'Few workers',
      notAvailable: 'No one working',
      notSure: 'Not sure',
    },
  },
  group_tables: {
    label: 'Group tables',
    emoji: '👥',
    question: 'Good for group work?',
    answers: {
      great: 'Perfect setup',
      adequate: 'Pretty good',
      couldImprove: 'Bit cramped',
      notAvailable: 'No group tables',
      notSure: 'Not sure',
    },
  },
  events_networking: {
    label: 'Events',
    emoji: '📅',
    question: 'Cool events happening?',
    answers: {
      great: 'Amazing events',
      adequate: 'Some events',
      couldImprove: 'Few events',
      notAvailable: 'No events',
      notSure: 'Not sure',
    },
  },
  music_low: {
    label: 'Music',
    emoji: '🔈',
    question: 'Music at a good level?',
    answers: {
      great: 'Perfect volume',
      adequate: 'Pretty good',
      couldImprove: 'Bit loud',
      notAvailable: 'No music',
      notSure: 'Not sure',
    },
  },

  // Food & Drink
  coffee: {
    label: 'Coffee',
    emoji: '☕',
    question: 'Coffee worth coming back for?',
    answers: {
      great: 'Amazing brew',
      adequate: 'Pretty good',
      couldImprove: 'Could be better',
      notAvailable: 'No coffee',
      notSure: 'Not sure',
    },
  },
  specialty_coffee: {
    label: 'Specialty coffee',
    emoji: '⭐',
    question: 'Specialty coffee on point?',
    answers: {
      great: 'Exceptional quality',
      adequate: 'Pretty decent',
      couldImprove: 'Not quite there',
      notAvailable: 'No specialty coffee',
      notSure: 'Not sure',
    },
  },
  food: {
    label: 'Food',
    emoji: '🍽️',
    question: 'Food options work for you?',
    answers: {
      great: 'Delicious options',
      adequate: 'Pretty good',
      couldImprove: 'Limited menu',
      notAvailable: 'No food',
      notSure: 'Not sure',
    },
  },
  vegetarian_options: {
    label: 'Vegetarian options',
    emoji: '🥗',
    question: 'Happy with veggie choices?',
    answers: {
      great: 'Great selection',
      adequate: 'Some options',
      couldImprove: 'Few choices',
      notAvailable: 'No veggie options',
      notSure: 'Not sure',
    },
  },
  vegan_options: {
    label: 'Vegan options',
    emoji: '🌱',
    question: 'Good vegan selection?',
    answers: {
      great: 'Excellent variety',
      adequate: 'Some choices',
      couldImprove: 'Few options',
      notAvailable: 'No vegan options',
      notSure: 'Not sure',
    },
  },
  gluten_free_options: {
    label: 'Gluten-free options',
    emoji: '🌾',
    question: 'Gluten-free covered?',
    answers: {
      great: 'Well covered',
      adequate: 'Some options',
      couldImprove: 'Few choices',
      notAvailable: 'No gluten-free',
      notSure: 'Not sure',
    },
  },
  alcohol: {
    label: 'Alcohol',
    emoji: '🍷',
    question: 'Drinks available when needed?',
    answers: {
      great: 'Great selection',
      adequate: 'Some drinks',
      couldImprove: 'Limited options',
      notAvailable: 'No alcohol',
      notSure: 'Not sure',
    },
  },
  free_tap_water: {
    label: 'Free tap water',
    emoji: '💧',
    question: 'Free water easy to get?',
    answers: {
      great: 'Always available',
      adequate: 'Pretty easy',
      couldImprove: 'Bit difficult',
      notAvailable: 'No free water',
      notSure: 'Not sure',
    },
  },

  // Space & Amenities
  natural_light: {
    label: 'Natural light',
    emoji: '☀️',
    question: 'Nice natural light?',
    answers: {
      great: 'Bright & airy',
      adequate: 'Pretty good',
      couldImprove: 'Bit dim',
      notAvailable: 'No natural light',
      notSure: 'Not sure',
    },
  },
  outdoor_area: {
    label: 'Outdoor area',
    emoji: '🌳',
    question: 'Outdoor spot any good?',
    answers: {
      great: 'Amazing space',
      adequate: 'Pretty nice',
      couldImprove: 'Bit cramped',
      notAvailable: 'No outdoor area',
      notSure: 'Not sure',
    },
  },
  spacious: {
    label: 'Spacious',
    emoji: '🏢',
    question: 'Enough space to breathe?',
    answers: {
      great: 'Very roomy',
      adequate: 'Decent space',
      couldImprove: 'Bit cramped',
      notAvailable: 'Too crowded',
      notSure: 'Not sure',
    },
  },
  restroom: {
    label: 'Restroom',
    emoji: '🚻',
    question: 'Restrooms clean & handy?',
    answers: {
      great: 'Very clean',
      adequate: 'Pretty clean',
      couldImprove: 'Could be cleaner',
      notAvailable: 'No restrooms',
      notSure: 'Not sure',
    },
  },
  accessible: {
    label: 'Accessible',
    emoji: '♿',
    question: 'Easy wheelchair access?',
    answers: {
      great: 'Fully accessible',
      adequate: 'Mostly accessible',
      couldImprove: 'Limited access',
      notAvailable: 'Not accessible',
      notSure: 'Not sure',
    },
  },
  air_conditioned: {
    label: 'AC',
    emoji: '❄️',
    question: 'Temperature just right?',
    answers: {
      great: 'Perfect temp',
      adequate: 'Pretty good',
      couldImprove: 'Too hot/cold',
      notAvailable: 'No AC',
      notSure: 'Not sure',
    },
  },
  heated: {
    label: 'Heated',
    emoji: '🔥',
    question: 'Warm enough in winter?',
    answers: {
      great: 'Nice & warm',
      adequate: 'Warm enough',
      couldImprove: 'Bit chilly',
      notAvailable: 'No heating',
      notSure: 'Not sure',
    },
  },
  smoke_free: {
    label: 'Smoke-free',
    emoji: '🚭',
    question: 'Totally smoke-free inside?',
    answers: {
      great: 'Completely smoke-free',
      adequate: 'Mostly smoke-free',
      couldImprove: 'Some smoking',
      notAvailable: 'Smoking allowed',
      notSure: 'Not sure',
    },
  },
  pet_friendly: {
    label: 'Pet friendly',
    emoji: '🐕',
    question: 'Pets welcome?',
    answers: {
      great: 'Very pet-friendly',
      adequate: 'Pets welcome',
      couldImprove: 'Limited pet policy',
      notAvailable: 'No pets allowed',
      notSure: 'Not sure',
    },
  },
  parking: {
    label: 'Parking',
    emoji: '🚗',
    question: 'Easy to park nearby?',
    answers: {
      great: 'Easy to find',
      adequate: 'Usually available',
      couldImprove: 'Quite difficult',
      notAvailable: 'No parking',
      notSure: 'Not sure',
    },
  },
  bike_parking: {
    label: 'Bike parking',
    emoji: '🚲',
    question: 'Safe spot for bikes?',
    answers: {
      great: 'Very secure',
      adequate: 'Pretty safe',
      couldImprove: 'Bit risky',
      notAvailable: 'No bike parking',
      notSure: 'Not sure',
    },
  },
  ebike_scooter_charging: {
    label: 'E-bike charging',
    emoji: '🔋',
    question: 'Can charge e-bikes/scooters?',
    answers: {
      great: 'Easy charging',
      adequate: 'Charging available',
      couldImprove: 'Limited charging',
      notAvailable: 'No charging',
      notSure: 'Not sure',
    },
  },

  // Extras
  water_refill_station: {
    label: 'Water refill',
    emoji: '🚰',
    question: 'Water refill handy?',
    answers: {
      great: 'Very convenient',
      adequate: 'Available',
      couldImprove: 'Bit awkward',
      notAvailable: 'No refill station',
      notSure: 'Not sure',
    },
  },
  books_corner: {
    label: 'Books corner',
    emoji: '📚',
    question: 'Nice book selection?',
    answers: {
      great: 'Great selection',
      adequate: 'Some books',
      couldImprove: 'Few books',
      notAvailable: 'No books',
      notSure: 'Not sure',
    },
  },
  board_games: {
    label: 'Board games',
    emoji: '🎲',
    question: 'Fun games to play?',
    answers: {
      great: 'Lots of games',
      adequate: 'Some games',
      couldImprove: 'Few games',
      notAvailable: 'No board games',
      notSure: 'Not sure',
    },
  },
  art_exhibitions: {
    label: 'Art exhibition',
    emoji: '🎨',
    question: 'Enjoying the art?',
    answers: {
      great: 'Amazing art',
      adequate: 'Pretty nice',
      couldImprove: 'Not much art',
      notAvailable: 'No art',
      notSure: 'Not sure',
    },
  },
  plants_greenery: {
    label: 'Plants',
    emoji: '🌿',
    question: 'Love the greenery?',
    answers: {
      great: 'Lots of greenery',
      adequate: 'Some plants',
      couldImprove: 'Few plants',
      notAvailable: 'No plants',
      notSure: 'Not sure',
    },
  },

  // Membership & Access
  affordability: {
    label: 'Affordable',
    emoji: '💰',
    question: 'Easy on the wallet?',
    answers: {
      great: 'Very affordable',
      adequate: 'Fair prices',
      couldImprove: 'Bit pricey',
      notAvailable: 'Expensive',
      notSure: 'Not sure',
    },
  },
  membership_required: {
    label: 'Membership required',
    emoji: '💳',
    question: 'Is membership/payment needed?',
    answers: {
      great: 'Free access',
      adequate: 'Reasonable cost',
      couldImprove: 'Bit expensive',
      notAvailable: 'Very expensive',
      notSure: 'Not sure',
    },
  },
  short_stays_only: {
    label: 'Short stays',
    emoji: '⏱️',
    question: 'Can you stay as long as you need?',
    answers: {
      great: 'No time limits',
      adequate: 'Reasonable limits',
      couldImprove: 'Strict limits',
      notAvailable: 'Very short stays',
      notSure: 'Not sure',
    },
  },
  day_passes_available: {
    label: 'Day passes available',
    emoji: '🎫',
    question: 'Can you buy day/session passes?',
    answers: {
      great: 'Great day passes',
      adequate: 'Day passes available',
      couldImprove: 'Limited passes',
      notAvailable: 'No day passes',
      notSure: 'Not sure',
    },
  },
};

export interface FilterGroup {
  label: string;
  keys: string[];
}

export const filterGroups: FilterGroup[] = [
  {
    label: 'Connectivity & Work',
    keys: [
      'wifi',
      'power_sockets',
      'long_stays_allowed',
      'work_friendly_tables',
      'quiet',
      'call_friendly',
      'printer_scanner',
      'private_rooms',
    ],
  },
  {
    label: 'Atmosphere',
    keys: [
      'crowd_level',
      'people_working',
      'group_tables',
      'events_networking',
      'music_low',
    ],
  },
  {
    label: 'Food & Drink',
    keys: [
      'coffee',
      'specialty_coffee',
      'food',
      'vegetarian_options',
      'vegan_options',
      'gluten_free_options',
      'alcohol',
      'free_tap_water',
    ],
  },
  {
    label: 'Space & Amenities',
    keys: [
      'natural_light',
      'outdoor_area',
      'spacious',
      'restroom',
      'accessible',
      'air_conditioned',
      'heated',
      'smoke_free',
      'pet_friendly',
      'parking',
      'bike_parking',
      'ebike_scooter_charging',
    ],
  },
  {
    label: 'Extras',
    keys: [
      'water_refill_station',
      'books_corner',
      'board_games',
      'art_exhibitions',
      'plants_greenery',
    ],
  },
  {
    label: 'Membership & Access',
    keys: [
      'affordability',
      'membership_required',
      'short_stays_only',
      'day_passes_available',
    ],
  },
];

export const getStaticFilter = (name: string) =>
  filtersStatic[name] ?? {
    label: name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' '),
    emoji: '⭐',
    question: `Is there ${name.replace(/_/g, ' ')}?`,
    answers: {
      great: 'Great',
      adequate: 'Adequate',
      couldImprove: 'Could improve',
      notAvailable: 'Not available',
      notSure: 'Not sure',
    },
  };
